import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Upload, ChevronRight, ChevronLeft, CheckCircle2, AlertCircle,
  Users, KeyRound, Mail, Send, RefreshCw, Copy, TrendingUp, Eye, EyeOff,
} from "lucide-react";

/**
 * Community Onboarding Wizard — R114.
 *
 * Four steps stitched into a single admin flow so a PM can go from
 * "here's a CSV of new residents" to "everyone has an account, temp
 * password, and email" without leaving the page.
 *
 *   1. Upload — pick a CSV + community
 *   2. Preview — the parser dry-runs and shows which rows will land
 *   3. Password mode — reset-link OR shared temp (with generator)
 *   4. Run — orchestrator creates users + fires passwords + tracks batch
 *
 * After Run, we redirect to the batch tracking page.
 */

function generatePassword(len = 14) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const symbols = "!@#$%&*";
  let out = "";
  for (let i = 0; i < len - 1; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  out += symbols[Math.floor(Math.random() * symbols.length)];
  return out;
}

function StepPill({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={
        "h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold " +
        (done ? "bg-emerald-500 text-white"
              : active ? "bg-orange-500 text-white"
              : "bg-white/[0.05] text-zinc-500 border border-white/10")
      }>
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
      </div>
      <span className={"text-xs " + (active || done ? "text-zinc-200" : "text-zinc-500")}>
        {label}
      </span>
    </div>
  );
}

export function OnboardingWizardPage() {
  const { propertyId } = useProperty();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mode, setMode] = useState("reset_link");
  const [sharedPw, setSharedPw] = useState("");
  const [reveal, setReveal] = useState(false);
  const [forceChange, setForceChange] = useState(true);
  const [batchName, setBatchName] = useState("");
  const [running, setRunning] = useState(false);

  const sharedPwValid = sharedPw.length >= 10
    && /[a-zA-Z]/.test(sharedPw) && /[0-9]/.test(sharedPw);

  const doPreview = useCallback(async () => {
    if (!file) return;
    if (!propertyId) {
      toast.warning("Pick a community from the top-right selector first.");
      return;
    }
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", propertyId);
      const { data } = await api.post("/admin/onboarding/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep(2);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "CSV parse failed");
    } finally { setPreviewLoading(false); }
  }, [file, propertyId]);

  const doRun = useCallback(async () => {
    if (!file || !propertyId) return;
    if (mode === "shared_temp" && !sharedPwValid) {
      toast.error("Shared password must be 10+ chars with a letter and a digit.");
      return;
    }
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", propertyId);
      fd.append("password_mode", mode);
      if (mode === "shared_temp") fd.append("shared_password", sharedPw);
      fd.append("force_change_on_login", String(forceChange));
      if (batchName) fd.append("batch_name", batchName);
      const { data } = await api.post("/admin/onboarding/run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Onboarding complete — ${data.created} accounts created`, {
        description: mode === "reset_link"
          ? `${data.invited} invites delivered`
          : `Shared temp password applied. Hand it out via ${forceChange ? "a channel of your choice — users will be forced to change on first login." : "SMS/phone as agreed."}`,
      });
      navigate(`/admin/onboarding/batch/${data.batch_id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Onboarding run failed");
    } finally { setRunning(false); }
  }, [file, propertyId, mode, sharedPw, sharedPwValid, forceChange, batchName, navigate]);

  const eligibleCount = preview?.ok_count || 0;

  return (
    <div className="p-8 space-y-6" data-testid="onboarding-wizard-page">
      <PageHeader
        title="Community onboarding wizard"
        subtitle="Bulk-create residents from a CSV, hand out temp passwords, and track who's activated their account."
      />

      <Card className="border-white/10 bg-white/[0.02] p-4">
        <div className="flex flex-wrap items-center gap-4">
          <StepPill n={1} label="Upload CSV" active={step === 1} done={step > 1} />
          <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
          <StepPill n={2} label="Preview" active={step === 2} done={step > 2} />
          <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
          <StepPill n={3} label="Password + delivery" active={step === 3} done={step > 3} />
          <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
          <StepPill n={4} label="Run" active={step === 4} done={false} />
          <div className="ml-auto text-[11px] text-zinc-500">
            {file && <>File: <span className="text-zinc-300">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB</>}
          </div>
        </div>
      </Card>

      {step === 1 && (
        <Card className="border-white/10 bg-white/[0.02] p-6" data-testid="onboarding-step-1">
          <div className="text-lg font-semibold mb-2">Upload the CSV</div>
          <p className="text-xs text-zinc-500 mb-4">
            Required headers: <b>name</b>, <b>email</b>. Optional: <b>role</b> (defaults to resident),
            <b> unit</b>, <b>phone</b>, <b>property_ids</b> (semicolon-separated, overrides the
            community picker if set). Excel-safe encoding (UTF-8 BOM) supported.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-orange-500 file:text-white file:hover:bg-orange-600 file:cursor-pointer text-zinc-400"
              data-testid="onboarding-file-input"
            />
            {file && (
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-300">
                {file.name}
              </Badge>
            )}
          </div>
          {!propertyId && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-xs text-amber-200 mb-4">
              Pick a community from the header selector first — every uploaded user will be attached to it (unless the CSV overrides via property_ids).
            </div>
          )}
          <Button onClick={doPreview} disabled={!file || previewLoading || !propertyId}
                   className="bg-orange-500 hover:bg-orange-600" data-testid="onboarding-preview-btn">
            {previewLoading ? "Parsing…" : "Preview rows"}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      )}

      {step === 2 && preview && (
        <Card className="border-white/10 bg-white/[0.02] p-6" data-testid="onboarding-step-2">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="text-lg font-semibold">Preview</div>
              <div className="text-xs text-zinc-500">
                <span className="text-emerald-300 font-semibold">{preview.ok_count}</span> ready ·{" "}
                <span className="text-rose-300 font-semibold">{preview.skip_count}</span> skipped
                {" "}({preview.duplicate_count} already exist)
              </div>
            </div>
            <Button variant="outline" onClick={() => setStep(1)} className="border-white/10 ml-auto">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button onClick={() => setStep(3)} disabled={eligibleCount === 0}
                     className="bg-orange-500 hover:bg-orange-600" data-testid="onboarding-continue-preview">
              Continue <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          <div className="max-h-96 overflow-y-auto rounded border border-white/10 divide-y divide-white/5">
            {preview.rows.map((r) => (
              <div key={r.row} className="p-2 flex items-start gap-2 text-xs" data-testid={`onboarding-row-${r.row}`}>
                {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5" />
                      : <AlertCircle className="h-3.5 w-3.5 text-rose-400 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className={r.ok ? "text-zinc-100" : "text-rose-300"}>
                    <span className="text-zinc-500">Row {r.row}:</span> {r.name || "—"}{" "}
                    <span className="text-zinc-500">·</span> {r.email || "—"}{" "}
                    <span className="text-zinc-500 text-[10px]">({r.role || "?"})</span>
                    {r.unit && <span className="text-zinc-500 text-[10px]"> · unit {r.unit}</span>}
                  </div>
                  {!r.ok && (
                    <div className="text-[10px] text-rose-400/80 mt-0.5">
                      {r.reason}
                      {r.already_exists && " — skip on run"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card className="border-white/10 bg-white/[0.02] p-6" data-testid="onboarding-step-3">
          <div className="text-lg font-semibold mb-2">Password + delivery</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setMode("reset_link")}
              className={"p-3 rounded-lg border text-left transition " +
                (mode === "reset_link" ? "border-orange-500/40 bg-orange-500/10" : "border-white/10 hover:bg-white/[0.03]")}
              data-testid="onboarding-mode-reset-link">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Mail className="h-3.5 w-3.5 text-orange-300" /> Email reset link
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Each resident gets their own magic link. Best when email works.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("shared_temp")}
              className={"p-3 rounded-lg border text-left transition " +
                (mode === "shared_temp" ? "border-orange-500/40 bg-orange-500/10" : "border-white/10 hover:bg-white/[0.03]")}
              data-testid="onboarding-mode-shared-temp">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <KeyRound className="h-3.5 w-3.5 text-orange-300" /> Shared temp password
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Everyone gets the same temp — you hand it out at the mailer / meeting.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("none")}
              className={"p-3 rounded-lg border text-left transition " +
                (mode === "none" ? "border-orange-500/40 bg-orange-500/10" : "border-white/10 hover:bg-white/[0.03]")}
              data-testid="onboarding-mode-none">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-3.5 w-3.5 text-orange-300" /> Skip — I&apos;ll handle later
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Just create the accounts. Send resets later from the users page.
              </div>
            </button>
          </div>

          {mode === "shared_temp" && (
            <div className="mt-4 space-y-3" data-testid="onboarding-shared-block">
              <div>
                <Label>Shared temporary password</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input type={reveal ? "text" : "password"} value={sharedPw}
                            onChange={(e) => setSharedPw(e.target.value)}
                            placeholder="10+ chars with a letter + digit"
                            className="bg-white/[0.04] border-white/10 pr-9"
                            data-testid="onboarding-shared-pw" />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                            onClick={() => setReveal(!reveal)}>
                      {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <Button type="button" variant="outline" className="border-white/10 shrink-0"
                          onClick={() => { setSharedPw(generatePassword()); setReveal(true); }}
                          data-testid="onboarding-shared-generate">Generate</Button>
                  {sharedPw && (
                    <Button type="button" variant="outline" size="icon" className="border-white/10 shrink-0"
                            onClick={() => { navigator.clipboard.writeText(sharedPw); toast.info("Copied"); }}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/10">
                <div>
                  <div className="text-xs font-medium">Force change on next login</div>
                  <div className="text-[11px] text-zinc-500">
                    Recommended — every resident lands on the first-login splash.
                  </div>
                </div>
                <Switch checked={forceChange} onCheckedChange={setForceChange} />
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label>Batch name <span className="text-zinc-500 text-[10px]">(optional)</span></Label>
            <Input value={batchName} onChange={(e) => setBatchName(e.target.value)}
                    placeholder="e.g., Building A move-in wave, Fall 2026"
                    className="mt-1 bg-white/[0.04] border-white/10"
                    data-testid="onboarding-batch-name" />
            <div className="text-[10px] text-zinc-500 mt-1">
              Helpful when you&apos;re running multiple onboarding waves — this label shows up on the tracking page.
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button variant="outline" onClick={() => setStep(2)} className="border-white/10">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button onClick={() => setStep(4)}
                     disabled={mode === "shared_temp" && !sharedPwValid}
                     className="bg-orange-500 hover:bg-orange-600"
                     data-testid="onboarding-continue-mode">
              Review & run <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card className="border-white/10 bg-white/[0.02] p-6" data-testid="onboarding-step-4">
          <div className="text-lg font-semibold mb-3">Ready to run</div>
          <ul className="text-sm text-zinc-300 space-y-2 mb-4">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              Create <b className="text-emerald-300">{eligibleCount}</b> new user{eligibleCount === 1 ? "" : "s"}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              Password mode: <b>{mode === "reset_link" ? "email reset links" : mode === "shared_temp" ? "shared temp password" : "no password action"}</b>
              {mode === "shared_temp" && forceChange && " (force change on first login)"}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              Stamp everyone with a <b>batch ID</b> so you can track activation rate over time.
            </li>
          </ul>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setStep(3)} className="border-white/10">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button onClick={doRun} disabled={running || eligibleCount === 0}
                     className="bg-emerald-500 hover:bg-emerald-600"
                     data-testid="onboarding-run">
              <Send className="h-3.5 w-3.5 mr-1" />
              {running ? "Running…" : `Onboard ${eligibleCount} resident${eligibleCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Onboarding batch list + detail (activation tracking)
// -----------------------------------------------------------------------------

export function OnboardingBatchesPage() {
  const [state, setState] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/onboarding/batches");
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load batches");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8 space-y-6" data-testid="onboarding-batches-page">
      <PageHeader
        title="Onboarding batches"
        subtitle="Every onboarding wave you've run, with live activation counts."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="border-white/10 h-9">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button onClick={() => navigate("/admin/onboarding")}
                     className="bg-orange-500 hover:bg-orange-600 h-9" data-testid="new-batch-btn">
              <Upload className="h-3.5 w-3.5 mr-1" /> New batch
            </Button>
          </div>
        }
      />
      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
      ) : state.items.length === 0 ? (
        <Card className="border-white/10 bg-white/[0.02] p-10 text-center text-sm text-zinc-500">
          No onboarding batches yet. Kick one off by uploading a CSV of new residents.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {state.items.map((b) => {
            const a = b.activation || {};
            return (
              <Card key={b.id} className="border-white/10 bg-white/[0.02] p-4 cursor-pointer hover:bg-white/[0.04] transition"
                     onClick={() => navigate(`/admin/onboarding/batch/${b.id}`)}
                     data-testid={`onboarding-batch-${b.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-sm font-semibold">{b.name}</div>
                  <Badge variant="outline" className="border-white/10 text-[10px] text-zinc-400 ml-auto">
                    {b.password_mode.replaceAll("_", " ")}
                  </Badge>
                </div>
                <div className="text-[11px] text-zinc-500">
                  {new Date(b.created_at).toLocaleString()} · by {b.created_by_name}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-emerald-300" />
                  <div>
                    <div className="text-2xl font-semibold text-emerald-300">{a.pct_activated || 0}%</div>
                    <div className="text-[10px] text-zinc-500">
                      {a.activated || 0} of {a.total || 0} activated · {a.pending_first_login || 0} pending
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


export function OnboardingBatchDetailPage() {
  const { bid } = useParams();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/onboarding/batches/${bid}`);
      setBatch(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load batch");
    } finally { setLoading(false); }
  }, [bid]);
  useEffect(() => { load(); }, [load]);

  const activated = batch?.activation?.activated || 0;
  const total = batch?.activation?.total || 0;
  const pct = batch?.activation?.pct_activated || 0;

  return (
    <div className="p-8 space-y-6" data-testid="onboarding-batch-detail-page">
      <PageHeader
        title={batch?.name || "Onboarding batch"}
        subtitle={batch && `${new Date(batch.created_at).toLocaleString()} · ${batch.total_rows} row${batch.total_rows === 1 ? "" : "s"} processed`}
        actions={
          <Button variant="outline" onClick={load} className="border-white/10 h-9">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        }
      />

      {loading || !batch ? (
        <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <Card className="border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center gap-4">
              <TrendingUp className={"h-10 w-10 " + (pct >= 75 ? "text-emerald-300" : pct >= 40 ? "text-amber-300" : "text-rose-300")} />
              <div className="flex-1">
                <div className="text-3xl font-semibold" data-testid="onboarding-batch-pct">{pct}%</div>
                <div className="text-sm text-zinc-400">
                  {activated} of {total} residents have signed in
                </div>
              </div>
              <Badge variant="outline" className="border-white/10 text-zinc-300">
                Mode: {batch.password_mode.replaceAll("_", " ")}
              </Badge>
            </div>
          </Card>

          <Card className="border-white/10 bg-white/[0.02] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs">
                <tr>
                  <th className="text-left px-3 py-2 text-zinc-400">User</th>
                  <th className="text-left px-3 py-2 text-zinc-400">Email</th>
                  <th className="text-left px-3 py-2 text-zinc-400">Role</th>
                  <th className="text-left px-3 py-2 text-zinc-400">Status</th>
                  <th className="text-left px-3 py-2 text-zinc-400">Last login</th>
                </tr>
              </thead>
              <tbody>
                {batch.users.map((u) => (
                  <tr key={u.id} className="border-t border-white/5" data-testid={`onboarding-user-${u.id}`}>
                    <td className="px-3 py-2 text-zinc-100">{u.name}</td>
                    <td className="px-3 py-2 text-zinc-300 text-xs">{u.email}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400">{u.role}</td>
                    <td className="px-3 py-2">
                      {u.last_login_at ? (
                        <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-[10px]">activated</Badge>
                      ) : u.require_password_change ? (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[10px]">temp password</Badge>
                      ) : (
                        <Badge variant="outline" className="border-zinc-500/30 text-zinc-400 text-[10px]">pending</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
